import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../lib/useStore.jsx';
import { appOf, today, exportStore, importStore, PROFILE_COLORS } from '../lib/store.js';
import { SKILLS } from '../content/skills.js';
import { noLongerHaveTo, ownedCount, awaitingVerification, visibleSkills } from '../domain/skills.js';
import { ageAt } from '../domain/age.js';
import { Avatar } from '../ui/Bits.jsx';
import { StarIcon } from '../ui/Icons.jsx';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function PinGate() {
  const { unlockParent, store } = useStore();
  const [pin, setPin] = useState('');
  const [bad, setBad] = useState(false);
  if (!store.parentPin) { unlockParent(''); return null; }
  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); if (!unlockParent(pin)) { setBad(true); setPin(''); } }}>
      <h1>Grown-ups</h1>
      <div className="field"><label htmlFor="pin">PIN</label>
        <input id="pin" className="input pin" inputMode="numeric" maxLength={4} value={pin} autoFocus onChange={(e) => { setBad(false); setPin(e.target.value.replace(/\D/g, '')); }} /></div>
      {bad && <p className="small" style={{ color: 'var(--danger)' }}>Not that one.</p>}
      <button className="btn primary block" type="submit" disabled={pin.length !== 4}>Open</button>
    </form>
  );
}

function EditChild({ profile }) {
  const { renameProfile, removeProfile, store } = useStore();
  const [name, setName] = useState(profile.name);
  const [birthMonth, setBirthMonth] = useState(profile.birthMonth);
  const [birthYear, setBirthYear] = useState(profile.birthYear);
  const [color, setColor] = useState(profile.color);
  const [confirm, setConfirm] = useState(false);
  const thisYear = Number(today().slice(0, 4));
  const save = (e) => { e.preventDefault(); renameProfile(profile.id, { name: name.trim() || profile.name, birthMonth, birthYear, color }); };
  return (
    <details className="card soft">
      <summary>Edit {profile.name}</summary>
      <form className="stack" onSubmit={save}>
        <div className="field"><label>Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}><label>Birth month</label>
            <select className="input" value={birthMonth} onChange={(e) => setBirthMonth(Number(e.target.value))}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></div>
          <div className="field" style={{ flex: 1 }}><label>Birth year</label>
            <select className="input" value={birthYear} onChange={(e) => setBirthYear(Number(e.target.value))}>{Array.from({ length: 20 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}</option>)}</select></div>
        </div>
        <div className="field"><label>Colour</label>
          <div className="row">{PROFILE_COLORS.map((c) => (
            <button key={c} type="button" className={`avatar c-${c}`} style={{ outline: c === color ? '3px solid var(--ink)' : 'none', outlineOffset: 3 }} aria-label={c} aria-pressed={c === color} onClick={() => setColor(c)}>{c === color ? '•' : ''}</button>
          ))}</div></div>
        <div className="actions">
          <button className="btn primary" type="submit">Save</button>
          {!confirm
            ? <button className="btn danger" type="button" onClick={() => setConfirm(true)} disabled={store.profiles.length === 1 && false}>Remove {profile.name}</button>
            : <button className="btn danger" type="button" onClick={() => removeProfile(profile.id)}>Really remove — this deletes their wall</button>}
        </div>
      </form>
    </details>
  );
}

function Backup() {
  const { store, replaceStore } = useStore();
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);
  const download = () => {
    const blob = new Blob([JSON.stringify(exportStore(store), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `can-do-backup-${today()}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const next = importStore(JSON.parse(await file.text()));
      replaceStore(next);
      setMsg(`Restored ${next.profiles.length} kid${next.profiles.length === 1 ? '' : 's'} from ${file.name}.`);
    } catch (err) {
      setMsg(`Couldn't read that: ${err.message}`);
    } finally {
      e.target.value = '';
    }
  };
  return (
    <details className="card soft">
      <summary>Backup</summary>
      <p className="small muted">Everything lives on this device only. Save a file now and then; restoring replaces what's here.</p>
      <div className="actions" style={{ marginTop: 10 }}>
        <button className="btn" type="button" onClick={download}>Save a backup file</button>
        <button className="btn" type="button" onClick={() => fileRef.current?.click()}>Restore from a file</button>
        <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onFile} />
      </div>
      {msg && <p className="small" style={{ marginTop: 8 }}>{msg}</p>}
    </details>
  );
}

export default function Parent() {
  const { store, parentUnlocked, lockParent, appendEvent, setActive, setPin } = useStore();
  const [newPin, setNewPin] = useState('');
  if (!parentUnlocked) return <PinGate />;

  const total = store.profiles.reduce((n, p) => n + noLongerHaveTo(SKILLS, appOf(p).events), 0);
  return (
    <div className="stack">
      <div className="spread"><h1>Family</h1><button className="btn small quiet" onClick={lockParent}>Lock</button></div>

      <section className="card tint-teal">
        <div className="stat"><span className="num">{total}</span><span>thing{total === 1 ? '' : 's'} you no longer have to do</span></div>
        <p className="small muted" style={{ marginTop: 6 }}>Every verified skill is one fewer thing to nag about. This number is the point.</p>
      </section>

      {store.profiles.length === 0 && <Link to="/setup" className="btn primary">Add your first kid</Link>}

      {store.profiles.map((p) => {
        const app = appOf(p);
        const queue = awaitingVerification(SKILLS, app, app.events);
        const owned = ownedCount(SKILLS, app.events);
        const onList = visibleSkills(SKILLS, app).length;
        return (
          <section key={p.id} className="card stack">
            <div className="row">
              <Avatar profile={p} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{p.name} <span className="muted small">· {ageAt(today(), p)}</span></div>
                <div className="small muted">{owned} independent · {onList} on the wall</div>
              </div>
              <Link to={`/parent/child/${p.id}`} className="btn small">Choose skills</Link>
            </div>
            {app.wishes.length > 0 && <p className="small muted">You wished: {app.wishes.join(' · ')}</p>}
            {queue.length > 0 && (
              <div className="stack">
                <p className="eyebrow">Ready for you to check</p>
                {queue.map((s) => (
                  <div key={s.id} className="spread">
                    <Link to={`/skill/${s.id}`} onClick={() => setActive(p.id)}>{s.title}</Link>
                    <button className="btn small accent" onClick={() => appendEvent(p.id, { skillId: s.id, type: 'verified', by: 'parent' })}><StarIcon width="18" height="18" /> Independent</button>
                  </div>
                ))}
              </div>
            )}
            <EditChild profile={p} />
          </section>
        );
      })}
      <Link to="/setup" className="btn">Add someone</Link>

      <Backup />

      <details className="card soft">
        <summary>Grown-up PIN</summary>
        <form className="row" onSubmit={(e) => { e.preventDefault(); setPin(newPin); setNewPin(''); }}>
          <input className="input" style={{ flex: 1 }} inputMode="numeric" maxLength={4} placeholder={store.parentPin ? 'new 4-digit PIN' : 'no PIN set — add one'} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))} />
          <button className="btn" type="submit" disabled={newPin.length !== 4}>Set</button>
          {store.parentPin && <button className="btn quiet" type="button" onClick={() => setPin('')}>Remove PIN</button>}
        </form>
      </details>
    </div>
  );
}
